import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	Input,
	OnDestroy,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";

/** Códigos que enseña el popover (subset del view model del listado/detalle). */
export interface ContractCodes {
	codigo?: string | null;
	codigoCorto?: string | null;
	cebePrincipalConstruccion?: string | null;
	cebePrincipalOperacion?: string | null;
}

/**
 * Popover de recodificación del contrato: al hacer hover sobre el código se
 * muestran los 4 códigos canónicos (matrícula + código corto + CeBe principal
 * de cada fase), para que cada departamento reconozca el contrato por el
 * código con el que lo conoce, ocupando cero espacio en el listado.
 *
 * Hover-only con retardo de apertura (no parpadea al pasear el ratón) y sin
 * interceptar el click de la fila (la navegación sigue funcionando).
 */
@Component({
	selector: "app-contract-codes",
	standalone: true,
	imports: [CommonModule, TranslateModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<span class="ccw" (mouseenter)="onEnter()" (mouseleave)="onLeave()">
			<ng-content></ng-content>
			<div class="ccp" *ngIf="open" role="tooltip" [class.is-below]="below" [style.left.px]="popLeft" [style.top.px]="popTop">
				<div class="ccp-h">{{ titleKey | translate }}</div>
				<div class="ccp-row is-main">
					<span class="ccp-lbl">{{ mainLabelKey | translate }}</span>
					<span class="ccp-val">{{ contract?.codigo || "—" }}</span>
				</div>
				<div class="ccp-row" *ngIf="contract?.codigoCorto">
					<span class="ccp-lbl">{{ "contracts.codes.corto" | translate }}</span>
					<span class="ccp-val">{{ contract?.codigoCorto }}</span>
				</div>
				<div class="ccp-row" *ngIf="contract?.cebePrincipalConstruccion">
					<span class="ccp-lbl">{{ "contracts.codes.cebeConstruccion" | translate }}</span>
					<span class="ccp-val">{{ contract?.cebePrincipalConstruccion }}</span>
				</div>
				<div class="ccp-row" *ngIf="contract?.cebePrincipalOperacion">
					<span class="ccp-lbl">{{ "contracts.codes.cebeOperacion" | translate }}</span>
					<span class="ccp-val">{{ contract?.cebePrincipalOperacion }}</span>
				</div>
			</div>
		</span>
	`,
	styles: [
		`
			:host {
				display: inline-block;
			}
			.ccw {
				position: relative;
				display: inline-block;
			}
			.ccp {
				/* fixed: escapa del overflow del contenedor de la tabla (el absolute
				   quedaba recortado en las primeras filas). Coordenadas medidas al abrir. */
				position: fixed;
				transform: translateY(-100%);
				z-index: 60;
				min-width: 230px;
				padding: 10px 12px;
				border-radius: 12px;
				background: #fff;
				border: 1px solid var(--line, #e6e3dd);
				box-shadow: 0 10px 28px rgba(28, 26, 23, 0.14);
				pointer-events: none; /* no roba el hover/click de la fila */
			}
			.ccp.is-below {
				transform: none;
			}
			.ccp-h {
				font-size: 10px;
				font-weight: 700;
				letter-spacing: 0.08em;
				text-transform: uppercase;
				color: var(--ink-3, #8a8a8a);
				margin-bottom: 6px;
			}
			.ccp-row {
				display: flex;
				align-items: baseline;
				justify-content: space-between;
				gap: 14px;
				padding: 2px 0;
			}
			.ccp-lbl {
				font-size: 11px;
				color: var(--ink-3, #8a8a8a);
				white-space: nowrap;
			}
			.ccp-val {
				font-family: var(--mono, monospace);
				font-size: 12px;
				color: var(--ink-2, #555);
				white-space: nowrap;
			}
			.ccp-row.is-main .ccp-val {
				font-weight: 700;
				color: var(--ink, #1c1a17);
			}
		`,
	],
})
export class CodesPopoverComponent implements OnDestroy {
	@Input() contract: ContractCodes | null = null;

	/** Título del popover (override para reusarlo fuera del contrato, p. ej. centros). */
	@Input() titleKey = "contracts.codes.popTitle";

	/** Etiqueta de la fila principal (Matrícula para contrato; Código para centro). */
	@Input() mainLabelKey = "contracts.codes.matricula";

	open = false;
	below = false;
	popLeft = 0;
	popTop = 0;
	private openTimer: number | null = null;

	constructor(
		private readonly cdr: ChangeDetectorRef,
		private readonly host: ElementRef<HTMLElement>,
	) {}

	onEnter(): void {
		this.clearTimer();
		this.openTimer = window.setTimeout(() => {
			// Posición fija medida sobre el anchor: arriba por defecto; si no hay
			// hueco (primeras filas), hacia abajo. Clamp horizontal al viewport.
			const rect = this.host.nativeElement.getBoundingClientRect();
			this.below = rect.top < 150;
			this.popLeft = Math.max(8, Math.min(rect.left, window.innerWidth - 260));
			this.popTop = this.below ? rect.bottom + 8 : rect.top - 8;
			this.open = true;
			this.cdr.markForCheck();
		}, 250);
	}

	onLeave(): void {
		this.clearTimer();
		if (this.open) {
			this.open = false;
			this.cdr.markForCheck();
		}
	}

	/** Con posición fija, el scroll/resize dejaría el popover flotando: se cierra. */
	@HostListener("window:wheel")
	@HostListener("window:resize")
	onViewportChange(): void {
		this.onLeave();
	}

	ngOnDestroy(): void {
		this.clearTimer();
	}

	private clearTimer(): void {
		if (this.openTimer !== null) {
			window.clearTimeout(this.openTimer);
			this.openTimer = null;
		}
	}
}
